import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { SignedTileLayer } from "./signedTileLayer";
import type { Plan, Marker } from "../../types/domain.types";
import type { PendingMarker } from "../../db/offlineDb";
import {
  classifyMarker,
  MARKER_STATUS_BG,
  MARKER_STATUS_LABEL,
} from "../../utils/markerColor";
import {
  MARKER_CATEGORY_ICON,
  MARKER_CATEGORY_LABEL,
} from "../../utils/markerCategory";

export type ViewerMode = "register" | "explore";
export type MarkerLabelField = "qrCode" | "comment";

interface Props {
  plan: Plan;
  markers: Marker[];
  pendingMarkers?: PendingMarker[];
  pendingCoord: { x: number; y: number } | null;
  onPickCoord: (coord: { x: number; y: number }) => void;
  mode?: ViewerMode;
  onMarkerVisit?: (markerId: string) => void;
  onMarkerDelete?: (markerId: string) => void;
  onMarkerEdit?: (marker: Marker) => void;
  /** Que dato mostrar como etiqueta sobre cada marcador. Default: qrCode. */
  labelField?: MarkerLabelField;
}

/**
 * Visor Leaflet con CRS.Simple.
 * Convenciones:
 *   - tileWidth/tileHeight: dimensiones totales del plano (px) al zoom maximo.
 *   - Coordenadas de marcador en espacio de plano (px al zoom max):
 *       coordX = lng
 *       coordY = -lat
 *   - bounds del mapa: [[-tileHeight, 0], [0, tileWidth]]
 *   - Tiles en formato XYZ estandar {z}/{x}/{y}.png
 */
export function PlanViewer({
  plan,
  markers,
  pendingMarkers = [],
  pendingCoord,
  onPickCoord,
  mode = "register",
  onMarkerVisit,
  onMarkerDelete,
  onMarkerEdit,
  labelField = "qrCode",
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const pendingMarkerRef = useRef<L.Marker | null>(null);
  // Callback siempre fresco sin recrear el mapa
  const onPickRef = useRef(onPickCoord);
  onPickRef.current = onPickCoord;
  const onMarkerVisitRef = useRef(onMarkerVisit);
  onMarkerVisitRef.current = onMarkerVisit;
  const onMarkerDeleteRef = useRef(onMarkerDelete);
  onMarkerDeleteRef.current = onMarkerDelete;
  const onMarkerEditRef = useRef(onMarkerEdit);
  onMarkerEditRef.current = onMarkerEdit;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // --- Init / destroy del mapa ---
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      crs: L.CRS.Simple,
      minZoom: plan.tileMinZoom,
      maxZoom: plan.tileMaxZoom,
      zoomControl: true,
      attributionControl: false,
      preferCanvas: false,
    });

    // tileWidth/tileHeight estan en px al zoom MAXIMO, pero CRS.Simple
    // interpreta 1 map unit = 1 pixel al zoom 0. Escalamos por 1/2^maxZoom
    // para que Leaflet pida tiles en el rango correcto.
    const scale = 1 / Math.pow(2, plan.tileMaxZoom);
    const bounds: L.LatLngBoundsLiteral = [
      [-plan.tileHeight * scale, 0],
      [0, plan.tileWidth * scale],
    ];
    // Nota: NO usamos map.setMaxBounds() para no bloquear/rebotar el
    // pan cuando el viewport es mas grande que el plano. El TileLayer
    // tiene su propio "bounds" que evita pedir tiles fuera del plano.
    // fitBounds real se aplica tras la primera medicion no-cero del
    // contenedor (ver ResizeObserver abajo).
    map.fitBounds(bounds);

    const tiles = new SignedTileLayer({
      planId: plan.id,
      tileSize: 256,
      minZoom: plan.tileMinZoom,
      maxZoom: plan.tileMaxZoom,
      noWrap: true,
      bounds,
    });
    tiles.addTo(map);

    const group = L.layerGroup().addTo(map);

    map.on("click", (ev: L.LeafletMouseEvent) => {
      // En modo explorador el click en espacio vacio no selecciona coord
      if (modeRef.current === "explore") return;
      const { lat, lng } = ev.latlng;
      // Convertimos de map units a px-al-zoom-max para guardar el marcador
      onPickRef.current({ x: lng / scale, y: -lat / scale });
    });

    // Ocultar etiquetas de IDs cuando el zoom esta por debajo de (maxZoom - 2)
    // para evitar traslape visual al alejarse. En zoom alto, las etiquetas
    // quedan separadas naturalmente.
    const labelZoomThreshold = Math.max(
      plan.tileMinZoom,
      plan.tileMaxZoom - 2,
    );
    const updateLabelVisibility = () => {
      const el = containerRef.current;
      if (!el) return;
      if (map.getZoom() < labelZoomThreshold) {
        el.classList.add("hide-marker-labels");
      } else {
        el.classList.remove("hide-marker-labels");
      }
    };
    map.on("zoomend", updateLabelVisibility);
    updateLabelVisibility();

    mapRef.current = map;
    tileLayerRef.current = tiles;
    markersLayerRef.current = group;

    // ResizeObserver: cuando el contenedor cambie de tamaño (responsive,
    // primer render con altura 0, etc.) invalidamos el tamaño interno de
    // Leaflet y, la primera vez que tengamos dimensiones reales, hacemos
    // un fit definitivo a los bounds del plano.
    let didInitialFit = false;
    const ro = new ResizeObserver(() => {
      const el = containerRef.current;
      if (!el) return;
      map.invalidateSize({ animate: false });
      if (!didInitialFit && el.clientWidth > 0 && el.clientHeight > 0) {
        map.fitBounds(bounds, { animate: false });
        didInitialFit = true;
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      markersLayerRef.current = null;
      pendingMarkerRef.current = null;
    };
  }, [plan.id, plan.tileMinZoom, plan.tileMaxZoom, plan.tileWidth, plan.tileHeight]);

  // --- Refresco periódico de cache de tiles firmadas (12 min) ---
  useEffect(() => {
    const id = setInterval(() => {
      const layer = tileLayerRef.current as unknown as {
        invalidateCache?: () => void;
        redraw?: () => void;
      } | null;
      layer?.invalidateCache?.();
    }, 12 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // --- Render marcadores persistidos ---
  const scale = 1 / Math.pow(2, plan.tileMaxZoom);
  useEffect(() => {
    const group = markersLayerRef.current;
    if (!group) return;
    group.clearLayers();
    const esc = (s: string) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    for (const m of markers) {
      const status = classifyMarker(m.lastVisitedAt);
      const bg = MARKER_STATUS_BG[status];
      const labelRaw =
        labelField === "comment" ? (m.comment ?? "") : m.qrCode;
      const labelTrimmed = labelRaw.trim();
      const labelText =
        labelTrimmed.length > 24
          ? `${labelTrimmed.slice(0, 22)}…`
          : labelTrimmed;
      const labelHtml = labelText
        ? `<div class="im-marker-label">${esc(labelText)}</div>`
        : "";
      const iconInner = m.category
        ? `<img src="${MARKER_CATEGORY_ICON[m.category]}" alt="${MARKER_CATEGORY_LABEL[m.category]}" style="width:32px;height:32px;display:block;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))" />`
        : `<div class="h-3 w-3 rounded-full ${bg} ring-2 ring-white shadow" style="margin:0 auto"></div>`;
      const iconHtml = `<div class="im-marker-wrap">${iconInner}${labelHtml}</div>`;
      // iconSize: ancho amplio para que la etiqueta tenga espacio horizontal;
      // iconAnchor: fija al centro del icono (la etiqueta va debajo via CSS).
      const iconSize: [number, number] = m.category ? [120, 48] : [120, 28];
      const iconAnchor: [number, number] = m.category ? [60, 16] : [60, 6];
      const icon = L.divIcon({
        className: "im-marker",
        html: iconHtml,
        iconSize,
        iconAnchor,
      });
      const latlng = L.latLng(-m.coordY * scale, m.coordX * scale);
      const mk = L.marker(latlng, { icon, keyboard: false });
      const safeComment = esc(m.comment ?? "");
      const safeQr = esc(m.qrCode);
      const safeUser = esc(m.user.email);
      const lastVisitLine = m.lastVisitedAt
        ? `<div class="mt-1 text-[11px] text-slate-600">${MARKER_STATUS_LABEL[status]}: ${new Date(m.lastVisitedAt).toLocaleString()}${m.lastVisitedBy ? ` por ${esc(m.lastVisitedBy.email)}` : ""}</div>`
        : `<div class="mt-1 text-[11px] text-slate-500">${MARKER_STATUS_LABEL[status]}</div>`;
      const visitButton =
        modeRef.current === "explore"
          ? `<button data-visit-id="${m.id}" class="mt-2 w-full rounded bg-emerald-600 px-2 py-1 text-white text-xs hover:bg-emerald-500">Confirmar visita</button>`
          : "";
      const editButton =
        modeRef.current === "register"
          ? `<button data-edit-id="${m.id}" class="mt-2 w-full rounded bg-blue-600 px-2 py-1 text-white text-xs hover:bg-blue-500">Editar marcador</button>`
          : "";
      const deleteButton =
        modeRef.current === "register"
          ? `<button data-delete-id="${m.id}" class="mt-1 w-full rounded bg-red-600 px-2 py-1 text-white text-xs hover:bg-red-500">Eliminar marcador</button>`
          : "";
      mk.bindPopup(
        `<div class="text-xs">
          <div><strong>QR:</strong> ${safeQr}</div>
          ${safeComment ? `<div class="mt-1">${safeComment}</div>` : ""}
          <div class="mt-1 text-slate-500">${safeUser}</div>
          ${lastVisitLine}
          ${visitButton}
          ${editButton}
          ${deleteButton}
        </div>`,
      );
      mk.on("popupopen", (ev) => {
        const el = ev.popup.getElement();
        if (!el) return;
        const btnVisit = el.querySelector<HTMLButtonElement>(
          `button[data-visit-id="${m.id}"]`,
        );
        if (btnVisit) {
          btnVisit.addEventListener("click", () => {
            onMarkerVisitRef.current?.(m.id);
            mk.closePopup();
          });
        }
        const btnEdit = el.querySelector<HTMLButtonElement>(
          `button[data-edit-id="${m.id}"]`,
        );
        if (btnEdit) {
          btnEdit.addEventListener("click", () => {
            onMarkerEditRef.current?.(m);
            mk.closePopup();
          });
        }
        const btnDelete = el.querySelector<HTMLButtonElement>(
          `button[data-delete-id="${m.id}"]`,
        );
        if (btnDelete) {
          btnDelete.addEventListener("click", () => {
            onMarkerDeleteRef.current?.(m.id);
            mk.closePopup();
          });
        }
      });
      mk.addTo(group);
    }

    // Pending (cola offline) con icono amber distintivo
    for (const p of pendingMarkers) {
      const icon = L.divIcon({
        className: "im-marker-offline",
        html: `<div class="h-3 w-3 rounded-full bg-amber-400 ring-2 ring-white shadow"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      const latlng = L.latLng(-p.coordY * scale, p.coordX * scale);
      const mk = L.marker(latlng, { icon, keyboard: false });
      const safeQr = p.qrCode
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      mk.bindPopup(
        `<div class="text-xs">
          <div><strong>QR:</strong> ${safeQr}</div>
          <div class="mt-1 text-amber-600">Pendiente de sincronizar</div>
        </div>`,
      );
      mk.addTo(group);
    }
  }, [markers, pendingMarkers, scale, mode, labelField]);

  // --- Marcador "pending" (coord seleccionada antes de guardar) ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pendingMarkerRef.current) {
      pendingMarkerRef.current.remove();
      pendingMarkerRef.current = null;
    }
    if (pendingCoord) {
      const icon = L.divIcon({
        className: "im-marker-pending",
        html: `<div class="h-4 w-4 rounded-full bg-amber-400 ring-2 ring-white animate-pulse"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const latlng = L.latLng(-pendingCoord.y * scale, pendingCoord.x * scale);
      const mk = L.marker(latlng, { icon, interactive: false });
      mk.addTo(map);
      pendingMarkerRef.current = mk;
    }
  }, [pendingCoord, scale]);

  return (
    <div
      ref={containerRef}
      className="h-full w-full bg-slate-950"
      style={{ minHeight: 320 }}
    />
  );
}
